import { Attachment } from '@medplum/fhirtypes';
import { IvrDigit, IvrKeyMapping, IvrTarget, isCompleteTarget } from '@bariendo/lib/ivr';
import { getProviderRoleLabel, IvrTargetKind, IvrTargetScope, ProviderRole } from '@bariendo/lib/constants';

/**
 * How long one rung rings before the chain moves on.
 *
 * Uniform and not configurable (BAR-2618): a per-rung timeout is a knob nobody can set
 * correctly, and the admin only needs the arithmetic to be visible. The runtime owns the
 * real dial timeout; this is the number the editor shows so the total is predictable.
 */
export const RUNG_RING_SECONDS = 30;

/**
 * The on-call NP rota is the one exception to the uniform ring length: an NP may be asleep,
 * and nothing sits below them in the chain, so they get longer to reach the phone.
 */
export const ON_CALL_RUNG_RING_SECONDS = 45;

export function rungRingSeconds(target: IvrTarget): number {
  return target.kind === IvrTargetKind.ON_CALL_NP ? ON_CALL_RUNG_RING_SECONDS : RUNG_RING_SECONDS;
}

/**
 * Roles offered as a rung, in menu order.
 *
 * The old control listed every role except Administrator, which put Behavioral Expert and
 * Emerson Quality Auditor in a phone menu. These five are the teams that answer calls;
 * {@link buildRungWhoOptions} still surfaces anything else a saved option already points at
 * so an existing configuration is never silently rewritten by opening the editor.
 */
export const RUNG_TEAM_ROLES: ProviderRole[] = [
  ProviderRole.PATIENT_COORDINATOR,
  ProviderRole.CARE_MANAGER,
  ProviderRole.PATIENT_NAVIGATOR,
  ProviderRole.PATIENT_ACCESS,
  ProviderRole.NURSE_PRACTITIONER,
];

const ROLE_VALUE_PREFIX = 'role:';

export interface RungWhoOption {
  value: string;
  label: string;
}

export interface RungWhoGroup {
  group: string;
  items: RungWhoOption[];
}

const OTHER_WHO_OPTIONS: RungWhoOption[] = [
  { value: IvrTargetKind.ON_CALL_NP, label: 'On-call NP rota' },
  { value: IvrTargetKind.SPECIFIC_PROVIDER, label: 'A specific team member' },
  { value: IvrTargetKind.EXTERNAL_NUMBER, label: 'An external number' },
  { value: IvrTargetKind.PLAY_AND_HANGUP, label: 'Play audio & hang up' },
];

export type IvrRoleTarget = Extract<IvrTarget, { kind: typeof IvrTargetKind.ROLE }>;

export function isRoleTarget(target: IvrTarget | undefined): target is IvrRoleTarget {
  return target?.kind === IvrTargetKind.ROLE;
}

/** The "Who" select's value for a rung, or null when the rung is unassigned. */
export function toRungWhoValue(target: IvrTarget | undefined): string | null {
  if (!target) {
    return null;
  }

  return target.kind === IvrTargetKind.ROLE ? `${ROLE_VALUE_PREFIX}${target.role}` : target.kind;
}

/**
 * Options for the "Who" select: teams first, then the kinds that are not a team.
 *
 * Scope is deliberately absent — a rung is Who plus a scope control, never the cross product
 * of the two in one dropdown (BAR-2618), which is what makes this list short enough to scan
 * down a table column.
 */
export function buildRungWhoOptions(target: IvrTarget | undefined): RungWhoGroup[] {
  const roles = [...RUNG_TEAM_ROLES];

  if (isRoleTarget(target) && !roles.includes(target.role)) {
    roles.push(target.role);
  }

  return [
    {
      group: 'Teams',
      items: roles.map((role) => ({ value: `${ROLE_VALUE_PREFIX}${role}`, label: getProviderRoleLabel(role) })),
    },
    { group: 'Other', items: OTHER_WHO_OPTIONS },
  ];
}

/**
 * The rung a freshly picked "Who" produces, carrying over what still applies.
 *
 * Scope survives a role change and a number survives being re-picked, so correcting the team
 * on a rung does not silently reset it to the pool. The two kinds that need a resource start
 * from an empty one rather than from nothing: the old control produced no action at all until
 * a file or practitioner was chosen, which is why the picker for it never appeared. An empty
 * resource fails `isCompleteTarget`, so it blocks save instead of being saved half-built.
 */
export function createTargetForWhoValue(value: string, previous?: IvrTarget): IvrTarget | undefined {
  if (value.startsWith(ROLE_VALUE_PREFIX)) {
    return {
      kind: IvrTargetKind.ROLE,
      role: value.slice(ROLE_VALUE_PREFIX.length) as ProviderRole,
      scope: isRoleTarget(previous) ? previous.scope : IvrTargetScope.POOL,
    };
  }

  switch (value) {
    case IvrTargetKind.ON_CALL_NP:
      return { kind: IvrTargetKind.ON_CALL_NP };
    case IvrTargetKind.EXTERNAL_NUMBER:
      return {
        kind: IvrTargetKind.EXTERNAL_NUMBER,
        phoneNumber: previous?.kind === IvrTargetKind.EXTERNAL_NUMBER ? previous.phoneNumber : '',
      };
    case IvrTargetKind.SPECIFIC_PROVIDER:
      return { kind: IvrTargetKind.SPECIFIC_PROVIDER, providerRef: {} };
    case IvrTargetKind.PLAY_AND_HANGUP:
      return { kind: IvrTargetKind.PLAY_AND_HANGUP, audioFile: {} };
    default:
      return undefined;
  }
}

/** The rung "+ Add fallback" appends: the first team not already in the chain, else PAC. */
export function nextFallbackTarget(targets: IvrTarget[]): IvrTarget {
  const usedRoles = new Set(targets.filter(isRoleTarget).map((target) => target.role));
  const role = RUNG_TEAM_ROLES.find((candidate) => !usedRoles.has(candidate)) ?? ProviderRole.PATIENT_ACCESS;

  return { kind: IvrTargetKind.ROLE, role, scope: IvrTargetScope.POOL };
}

/**
 * The first enabled option holding a rung that cannot be dialled — an external number with
 * no number, a recording with no file, a team member never picked.
 *
 * `prepareKeyMappingForSave` drops such a rung, which is right for a chain someone left
 * half-edited but wrong as the only feedback: the option would quietly save one rung shorter
 * than what is on screen. The editor blocks the save and names the digit instead.
 */
export function findIncompleteRungDigit(keyMappings: IvrKeyMapping[]): IvrDigit | undefined {
  for (const mapping of keyMappings) {
    if (mapping.enabled === false) {
      continue;
    }

    const rungs = [...mapping.targets, ...(mapping.afterHoursTargets ?? [])];

    if (rungs.some((target) => !isCompleteTarget(target))) {
      return mapping.digit;
    }
  }

  return undefined;
}

/**
 * The goodbye recording, which the editor presents once for the whole menu even though it is
 * stored per option.
 *
 * `IvrKeyMapping.endOfChainAudio` is per-mapping because the runtime reads it while walking
 * one option's chain, but "when the chain runs out" is a property of the menu, not of digit
 * 3. The editor therefore reads whichever option carries one and writes it to all of them on
 * save; see {@link applyEndOfChainAudio}.
 */
export function findEndOfChainAudio(keyMappings: IvrKeyMapping[]): Attachment | undefined {
  return keyMappings.find((mapping) => mapping.endOfChainAudio?.url)?.endOfChainAudio;
}

export function applyEndOfChainAudio(keyMappings: IvrKeyMapping[], audioFile: Attachment | undefined): IvrKeyMapping[] {
  return keyMappings.map((mapping) => ({ ...mapping, endOfChainAudio: audioFile }));
}
